import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  EditorDeContrato,
  padronizarHtml,
  type EditorDeContratoHandle,
} from '../../componentes/EditorDeContrato';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Cartao,
  EstadoCarregando,
  MenuDeAcoes,
  Modal,
  Selo,
  useDialogo,
  type TomSemantico,
} from '../../componentes/ui';
import {
  CHAVE_DOS_MODELOS,
  alternarModelo,
  cancelarEnvio,
  carregarAgendados,
  carregarDoPostmark,
  carregarEnvios,
  carregarModelos,
  carregarVersoes,
  comoInserir,
  criarNovoEmail,
  criarVariaveisDoEmail,
  descreverDia,
  excluirEmail,
  previaDoModelo,
  reconhecerVariaveisDoEmail,
  renomearEmail,
  salvarModelo,
  salvarRegra,
  salvarMoldura,
  testarModelo,
  type EmailDoDeca,
  type EnvioDoEmail,
  type Moldura,
  type MolduraNaTela,
  type DiaDoMes,
  type QuandoDoEmail,
  type RegraDoEmail,
  type SituacaoDoEnvio,
} from '../../servicos/modelosDeEmail';
import {
  CHAVE_DAS_AUTOMACOES,
  carregarAutomacoes,
  regraDoEmail,
  type Automacao,
} from '../../servicos/automacoes';
import { ConfirmarVariaveis } from '../../componentes/ConfirmarVariaveis';
import { ErroDaApi } from '../ErroDaApi';
import { ListaDeAutomacoes, NovaAutomacao, mandaOEmail } from './Automacoes';
import { EditorDeAutomacao, type RascunhoDeAutomacao } from './EditorDeAutomacao';
import { agruparEmails } from './gruposDeEmails';
import { NumeroDoPasso } from './NumeroDoPasso';

/**
 * A aba E-mails da Central de Configurações (D-017).
 *
 * O texto de cada e-mail que o Deca manda, editado aqui; o Postmark só
 * entrega. Cada e-mail vira **um por um**: enquanto o modelo do Deca está
 * desligado, sai o template do Postmark de sempre — a pessoa testa, manda um
 * teste para si e só então liga.
 */
export function ModelosDeEmail(): JSX.Element {
  const [parametros, setParametros] = useSearchParams();
  const consulta = useQuery({ queryKey: CHAVE_DOS_MODELOS, queryFn: carregarModelos });
  const aberto = parametros.get('email');

  const abrir = (id: string | null): void => {
    const proximos = new URLSearchParams(parametros);
    if (id === null) proximos.delete('email');
    else proximos.set('email', id);
    setParametros(proximos, { replace: false });
  };

  if (consulta.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os e-mails" erro={consulta.error} />;
  }
  if (consulta.data === undefined) {
    return <EstadoCarregando variante="cartao" contexto="os e-mails" />;
  }

  const email = consulta.data.emails.find((e) => e.id === aberto);
  if (email !== undefined) {
    return (
      <EditorDoModelo
        key={email.id}
        email={email}
        moldura={consulta.data.moldura}
        aoVoltar={() => abrir(null)}
      />
    );
  }
  return (
    <ListaDeEmails emails={consulta.data.emails} moldura={consulta.data.moldura} aoAbrir={abrir} />
  );
}

/* ─── A lista ─────────────────────────────────────────────────────────────── */

interface Estado {
  readonly tom: TomSemantico;
  readonly texto: string;
}

/** Se o e-mail está saindo: a automação do sistema ligada, ou as automações dos e-mails novos. */
function envio(email: EmailDoDeca, automacoes: readonly Automacao[] | undefined): Estado {
  if (email.personalizado) {
    if (automacoes === undefined) return { tom: 'neutro', texto: 'Sai pelas automações' };
    const delas = automacoes.filter((a) => mandaOEmail(a, email.id));
    const ligadas = delas.filter((a) => a.situacao === 'ATIVA').length;
    if (delas.length === 0) return { tom: 'atencao', texto: 'Nenhuma automação manda' };
    return ligadas > 0
      ? { tom: 'positivo', texto: `Saindo — ${ligadas} automação(ões)` }
      : { tom: 'neutro', texto: 'Automações desligadas' };
  }
  return email.automacao?.regra.ativa === false
    ? { tom: 'atencao', texto: 'Desligado — não sai' }
    : { tom: 'positivo', texto: 'Saindo' };
}

/** Qual texto sai: o do Postmark, o do Deca, ou o simples automático. */
function texto(email: EmailDoDeca): Estado | null {
  if (email.personalizado) {
    return email.problemas.length > 0
      ? { tom: 'atencao', texto: `Texto com ${email.problemas.length} pendência(s)` }
      : null;
  }
  if (email.modelo?.ativo === true) return { tom: 'informativo', texto: 'Texto do Deca' };
  if (email.modelo !== null && email.problemas.length > 0) {
    return { tom: 'atencao', texto: 'Texto do Deca com pendência (não ligado)' };
  }
  if (email.modelo !== null) return { tom: 'neutro', texto: 'Texto do Deca pronto (não ligado)' };
  if (email.templatePostmark !== null) return { tom: 'neutro', texto: 'Texto do Postmark' };
  return { tom: 'neutro', texto: 'Texto simples automático' };
}

function ListaDeEmails(props: {
  readonly emails: readonly EmailDoDeca[];
  readonly moldura: Moldura;
  readonly aoAbrir: (id: string) => void;
}): JSX.Element {
  const grupos = useMemo(() => agruparEmails(props.emails), [props.emails]);
  const ligados = props.emails.filter((e) => e.modelo?.ativo === true).length;
  const automacoes = useQuery({ queryKey: CHAVE_DAS_AUTOMACOES, queryFn: carregarAutomacoes });
  const dialogo = useDialogo();
  const queryClient = useQueryClient();
  const criar = useMutation({
    mutationFn: criarNovoEmail,
    onSuccess: async ({ tipo }) => {
      await queryClient.invalidateQueries({ queryKey: CHAVE_DOS_MODELOS });
      void queryClient.invalidateQueries({ queryKey: CHAVE_DAS_AUTOMACOES });
      props.aoAbrir(tipo);
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <Aviso tom="informacao" titulo="Como funciona" className="flex-1">
          Tudo sobre cada e-mail fica aqui: o texto, quando ele sai e para quem — abra o e-mail para
          mudar. Na lista, o primeiro selo diz se ele <strong>está saindo</strong>; o segundo,{' '}
          <strong>qual texto sai</strong> (o do Postmark, até você ligar o do Deca). Os{' '}
          <strong>e-mails novos</strong> são seus: você escreve e diz, na página dele, quando sai.{' '}
          {ligados > 0 && `${ligados} do sistema já sai(em) com o texto do Deca.`}
        </Aviso>
        <Botao
          carregando={criar.isPending}
          onClick={async () => {
            const nome = await dialogo.pedirTexto({
              titulo: 'Novo e-mail',
              descricao:
                'Dê um nome para você reconhecer o e-mail (ex.: "Agradecimento pelo fechamento"). Depois você escreve o texto e diz quando ele sai.',
              rotulo: 'Nome do e-mail',
              obrigatorio: true,
              rotuloConfirmar: 'Criar',
            });
            if (nome !== null && nome.trim() !== '') criar.mutate(nome.trim());
          }}
        >
          + Novo e-mail
        </Botao>
      </div>
      {criar.error !== null && <ErroDaApi titulo="Não foi possível criar" erro={criar.error} />}

      <Importacao emails={props.emails} />
      <ResumoDaMoldura moldura={props.moldura} />

      {grupos.map((grupo) => (
        <Cartao
          key={grupo.titulo}
          titulo={grupo.titulo}
          {...(grupo.nota === null ? {} : { subtitulo: grupo.nota })}
          densidade="compacta"
        >
          <ul className="flex flex-col divide-y divide-borda">
            {grupo.linhas.map(({ email: e, numero, quando }) => {
              const saida = envio(e, automacoes.data);
              const qualTexto = texto(e);
              return (
                <li key={e.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                  <NumeroDoPasso
                    numero={numero}
                    alinhar={grupo.linhas.some((l) => l.numero !== null)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-conteudo-primario">{e.rotulo}</p>
                    <p className="text-sm text-conteudo-secundario">{quando}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:w-72 sm:justify-end">
                    <Selo tom={saida.tom} tamanho="pequeno">
                      {saida.texto}
                    </Selo>
                    {qualTexto !== null && (
                      <Selo tom={qualTexto.tom} tamanho="pequeno" semPonto>
                        {qualTexto.texto}
                      </Selo>
                    )}
                  </div>
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    onClick={() => props.aoAbrir(e.id)}
                  >
                    {e.modelo === null ? 'Criar modelo' : 'Abrir'}
                  </Botao>
                </li>
              );
            })}
          </ul>
        </Cartao>
      ))}
    </div>
  );
}

/**
 * Traz para o Deca o texto atual dos e-mails que ainda só existem no Postmark.
 *
 * Cada um passa pelo editor antes de ser gravado (`padronizarHtml`): o que
 * fica salvo é o que a pessoa vê ao abrir, sem as classes e o CSS de lá. Nasce
 * **desligado** — nada muda no envio até alguém ligar.
 */
function Importacao(props: { readonly emails: readonly EmailDoDeca[] }): JSX.Element | null {
  const queryClient = useQueryClient();
  const [progresso, setProgresso] = useState<string | null>(null);
  const pendentes = props.emails.filter((e) => e.modelo === null && e.templatePostmark !== null);

  const importar = useMutation({
    mutationFn: async () => {
      setProgresso('Lendo o Postmark…');
      const templates = await carregarDoPostmark();
      let feitos = 0;
      for (const t of templates) {
        setProgresso(`Trazendo ${++feitos} de ${templates.length}…`);
        await salvarModelo(t.tipo, {
          assunto: t.assunto,
          corpo: padronizarHtml(t.corpo, 'email'),
          templatePostmark: t.templatePostmark,
          motivo: 'IMPORTADO',
        });
      }
      return templates.length;
    },
    onSettled: () => {
      setProgresso(null);
      void queryClient.invalidateQueries({ queryKey: CHAVE_DOS_MODELOS });
    },
  });

  if (pendentes.length === 0 && importar.data === undefined) return null;

  return (
    <div className="flex flex-col gap-3">
      {pendentes.length > 0 && (
        <Aviso
          tom="atencao"
          titulo={`${pendentes.length} e-mail(s) ainda só existem no Postmark`}
          acao={
            <Botao
              tamanho="pequeno"
              carregando={importar.isPending}
              onClick={() => importar.mutate()}
            >
              Importar do Postmark
            </Botao>
          }
        >
          {progresso ??
            'Traz o assunto e o texto de cada um para cá, já na moldura do Deca. Eles chegam desligados: o envio não muda até você ligar.'}
        </Aviso>
      )}
      {importar.data !== undefined && (
        <Aviso tom="positivo" aoFechar={() => importar.reset()}>
          {importar.data} modelo(s) importado(s). Abra cada um, confira a pré-visualização e ligue
          quando estiver bom.
        </Aviso>
      )}
      {importar.error !== null && (
        <ErroDaApi titulo="A importação parou no meio" erro={importar.error} />
      )}
    </div>
  );
}

/* ─── A moldura ───────────────────────────────────────────────────────────── */

function ResumoDaMoldura(props: { readonly moldura: Moldura }): JSX.Element {
  const [editando, setEditando] = useState(false);
  return (
    <Cartao
      titulo="Moldura dos e-mails"
      subtitulo="O cabeçalho (logo ou nome) e o rodapé que todo e-mail do Deca veste."
      acao={
        <Botao variante="secundario" tamanho="pequeno" onClick={() => setEditando(true)}>
          Editar moldura
        </Botao>
      }
      densidade="compacta"
    >
      <div className="flex items-center gap-3 text-sm text-conteudo-secundario">
        <span
          aria-hidden="true"
          className="h-5 w-5 rounded-full border border-borda"
          style={{ background: props.moldura.cor }}
        />
        {props.moldura.logoUrl === null ? (
          <span>
            Cabeçalho com o nome <strong>{props.moldura.nome}</strong> (sem logo)
          </span>
        ) : (
          <img src={props.moldura.logoUrl} alt={props.moldura.nome} className="h-8 w-auto" />
        )}
      </div>
      {editando && <EditorDaMoldura moldura={props.moldura} aoFechar={() => setEditando(false)} />}
    </Cartao>
  );
}

function EditorDaMoldura(props: {
  readonly moldura: Moldura;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [dados, setDados] = useState<MolduraNaTela>({
    nome: props.moldura.nome,
    logoUrl: props.moldura.logoUrl ?? '',
    cor: props.moldura.cor,
    rodape: props.moldura.rodape,
  });
  const [previa, setPrevia] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: () => salvarMoldura(dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHAVE_DOS_MODELOS });
      props.aoFechar();
    },
  });
  const verPrevia = useMutation({
    mutationFn: () =>
      previaDoModelo({
        tipo: 'RECUPERACAO_SENHA',
        assunto: 'Exemplo',
        corpo:
          '<p>Olá, Maria.</p><p>Este é um exemplo do corpo de um e-mail, para ver a moldura em volta. ' +
          '<a href="https://decacapital.com.br">Um link fica assim.</a></p>',
        moldura: dados,
      }),
    onSuccess: (r) => setPrevia(r.html),
  });

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo="Moldura dos e-mails"
      descricao="Vale para todos os e-mails que saem pelo modelo do Deca."
      largura="ampla"
      rodape={
        <div className="flex flex-wrap justify-end gap-2">
          <Botao variante="sutil" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao
            variante="secundario"
            carregando={verPrevia.isPending}
            onClick={() => verPrevia.mutate()}
          >
            Pré-visualizar
          </Botao>
          <Botao carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            Salvar moldura
          </Botao>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {(salvar.error ?? verPrevia.error) !== null && (
          <ErroDaApi titulo="Não foi possível" erro={salvar.error ?? verPrevia.error} />
        )}
        <div className="grid gap-4 sm:grid-cols-[1fr_1fr_10rem]">
          <Campo
            rotulo="Nome no cabeçalho"
            ajuda="Aparece quando não há logo, e como texto alternativo do logo."
            value={dados.nome}
            onChange={(e) => setDados({ ...dados, nome: e.target.value })}
          />
          <Campo
            rotulo="Endereço do logo"
            ajuda="Opcional. Um endereço https:// público da imagem; ela aparece com 40 px de altura."
            placeholder="https://…/logo.png"
            value={dados.logoUrl}
            onChange={(e) => setDados({ ...dados, logoUrl: e.target.value })}
          />
          <label className="flex flex-col gap-1.5 text-sm font-medium text-conteudo-primario">
            Cor de destaque
            <span className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Escolher a cor"
                value={dados.cor}
                onChange={(e) => setDados({ ...dados, cor: e.target.value })}
                className="h-10 w-12 cursor-pointer rounded-card border border-borda bg-superficie-base"
              />
              <span className="font-mono text-xs text-conteudo-secundario">{dados.cor}</span>
            </span>
          </label>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-conteudo-primario">Rodapé</span>
          <EditorDeContrato
            modo="email"
            value={dados.rodape}
            onChange={(rodape) => setDados((d) => ({ ...d, rodape }))}
            variaveis={[]}
            placeholder="O texto do rodapé: contatos, aviso de e-mail automático…"
            className="[&_.ProseMirror]:min-h-[8rem]"
          />
        </div>
        {previa !== null && <PreviaEmIframe html={previa} />}
      </div>
    </Modal>
  );
}

/* ─── O editor de um e-mail ───────────────────────────────────────────────── */

/** As variáveis do texto que o e-mail não recebe — o aviso imediato; a palavra final é do servidor. */
function variaveisDesconhecidas(texto: string, email: EmailDoDeca): string[] {
  const conhecidas = new Set(email.variaveis.map((v) => v.nome));
  const usadas = new Set<string>();
  let dentroDeRepeticao = 0;
  const limpo = texto
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&nbsp;/g, ' ');
  for (const m of limpo.matchAll(/\{\{\{?\s*([#^/&]?)\s*([^{}]*?)\s*\}\}\}?/g)) {
    const [, sinal, nome = ''] = m;
    if (sinal === '#' && /^each\s/.test(nome)) dentroDeRepeticao++;
    if (sinal === '/' && /^each\b/.test(nome))
      dentroDeRepeticao = Math.max(0, dentroDeRepeticao - 1);
    if (sinal === '/' || nome === '.' || dentroDeRepeticao > 0) continue;
    const raiz = nome.replace(/^each\s+/, '').split('.')[0]!;
    if (raiz !== '' && !conhecidas.has(raiz)) usadas.add(raiz);
  }
  return [...usadas];
}

function EditorDoModelo(props: {
  readonly email: EmailDoDeca;
  readonly moldura: Moldura;
  readonly aoVoltar: () => void;
}): JSX.Element {
  const { email } = props;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const editorRef = useRef<EditorDeContratoHandle>(null);
  const assuntoRef = useRef<HTMLInputElement>(null);
  const [alvo, setAlvo] = useState<'assunto' | 'corpo'>('corpo');

  const salvo = { assunto: email.modelo?.assunto ?? '', corpo: email.modelo?.corpo ?? '' };
  const [assunto, setAssunto] = useState(salvo.assunto);
  const [corpo, setCorpo] = useState(salvo.corpo);
  const [restaurada, setRestaurada] = useState(false);
  const sujo = assunto !== salvo.assunto || corpo !== salvo.corpo;

  const [previa, setPrevia] = useState<{ assunto: string; html: string } | null>(null);
  const [historico, setHistorico] = useState(false);
  /**
   * D-022 — as variáveis novas do e-mail, confirmadas antes de salvar
   * (`'salvar'`) ou pelo atalho do aviso (`'so-criar'`).
   */
  const [confirmando, setConfirmando] = useState<'salvar' | 'so-criar' | null>(null);
  const recarregar = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: CHAVE_DOS_MODELOS });

  const salvar = useMutation({
    mutationFn: () =>
      salvarModelo(email.id, { assunto, corpo, motivo: restaurada ? 'RESTAURADO' : 'EDITADO' }),
    onSuccess: async () => {
      setRestaurada(false);
      await recarregar();
    },
  });
  const alternar = useMutation({
    mutationFn: (ativo: boolean) => alternarModelo(email.id, ativo),
    onSettled: recarregar,
  });
  const verPrevia = useMutation({
    mutationFn: () => previaDoModelo({ tipo: email.id, assunto, corpo }),
    onSuccess: (r) => setPrevia(r),
  });
  const testar = useMutation({
    mutationFn: () => testarModelo({ tipo: email.id, assunto, corpo }),
  });

  const desconhecidas = useMemo(
    () => variaveisDesconhecidas(`${assunto} ${corpo}`, email),
    [assunto, corpo, email],
  );
  const ativo = email.modelo?.ativo === true;

  const renomear = async (): Promise<void> => {
    const nome = await dialogo.pedirTexto({
      titulo: 'Renomear o e-mail',
      rotulo: 'Nome',
      valorInicial: email.rotulo,
      obrigatorio: true,
    });
    if (nome === null || nome.trim() === '' || nome.trim() === email.rotulo) return;
    await renomearEmail(email.id, nome.trim());
    await recarregar();
    void queryClient.invalidateQueries({ queryKey: CHAVE_DAS_AUTOMACOES });
  };
  const excluirMutacao = useMutation({
    mutationFn: () => excluirEmail(email.id),
    onSuccess: async () => {
      await recarregar();
      props.aoVoltar();
    },
  });
  const excluir = async (): Promise<void> => {
    const ok = await dialogo.confirmar({
      titulo: `Excluir o e-mail "${email.rotulo}"?`,
      descricao:
        'O texto e o histórico de versões somem, e os envios ainda agendados são cancelados. Se alguma automação manda este e-mail, o Deca não deixa excluir — tire o e-mail dela antes.',
      rotuloConfirmar: 'Excluir',
      tom: 'perigo',
    });
    if (ok) excluirMutacao.mutate();
  };
  const erro =
    salvar.error ?? alternar.error ?? verPrevia.error ?? testar.error ?? excluirMutacao.error;

  const inserir = (texto: string): void => {
    if (alvo === 'corpo') {
      editorRef.current?.inserir(texto);
      return;
    }
    const campo = assuntoRef.current;
    const inicio = campo?.selectionStart ?? assunto.length;
    const fim = campo?.selectionEnd ?? assunto.length;
    setAssunto(assunto.slice(0, inicio) + texto + assunto.slice(fim));
    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(inicio + texto.length, inicio + texto.length);
    });
  };

  const voltar = async (): Promise<void> => {
    if (
      sujo &&
      !(await dialogo.confirmar({
        titulo: 'Sair sem salvar?',
        descricao: 'As alterações deste e-mail se perdem.',
        rotuloConfirmar: 'Sair sem salvar',
        tom: 'perigo',
      }))
    ) {
      return;
    }
    props.aoVoltar();
  };

  const alternarComConfirmacao = async (): Promise<void> => {
    const ok = await dialogo.confirmar(
      ativo
        ? {
            titulo: `Voltar "${email.rotulo}" para o Postmark?`,
            descricao:
              email.templatePostmark === null
                ? 'Os próximos envios saem com o texto simples automático. O modelo continua salvo aqui.'
                : `Os próximos envios saem com o template ${email.templatePostmark} do Postmark. O modelo continua salvo aqui.`,
            rotuloConfirmar: 'Desligar',
          }
        : {
            titulo: `Ligar o modelo do Deca para "${email.rotulo}"?`,
            descricao:
              'A partir do próximo envio, este e-mail sai com este texto e a moldura do Deca, para clientes, fornecedores e assessores de verdade.\n\nConfira antes a pré-visualização e o e-mail de teste.',
            rotuloConfirmar: 'Ligar',
          },
    );
    if (ok) alternar.mutate(!ativo);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start gap-3">
        <Botao variante="sutil" tamanho="pequeno" onClick={() => void voltar()}>
          ← Todos os e-mails
        </Botao>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-lg font-semibold tracking-tight">{email.rotulo}</h2>
          <p className="text-sm text-conteudo-secundario">{email.quando}</p>
        </div>
        {email.personalizado && (
          <MenuDeAcoes
            rotulo={email.rotulo}
            itens={[
              { rotulo: 'Renomear', aoEscolher: () => void renomear() },
              { rotulo: 'Excluir', perigo: true, aoEscolher: () => void excluir() },
            ]}
          />
        )}
      </div>

      {email.personalizado ? (
        !sujo &&
        email.problemas.length > 0 && (
          <Aviso
            tom="atencao"
            titulo="Enquanto houver pendência, as automações não mandam este e-mail"
          >
            <ul className="list-disc pl-5">
              {email.problemas.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </Aviso>
        )
      ) : (
        <Cartao densidade="compacta">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex-1 text-sm">
              {ativo ? (
                <p>
                  <strong>Saindo pelo modelo do Deca.</strong> Cada alteração salva vale a partir do
                  próximo envio.
                </p>
              ) : (
                <p>
                  <strong>
                    Saindo{' '}
                    {email.templatePostmark === null
                      ? 'com o texto simples automático'
                      : `pelo template ${email.templatePostmark} do Postmark`}
                    .
                  </strong>{' '}
                  O modelo daqui só vale depois de ligado.
                </p>
              )}
            </div>
            <Botao
              variante={ativo ? 'secundario' : 'primario'}
              carregando={alternar.isPending}
              disabled={email.modelo === null || sujo}
              title={sujo ? 'Salve antes de ligar ou desligar.' : undefined}
              onClick={() => void alternarComConfirmacao()}
            >
              {ativo ? 'Voltar para o Postmark' : 'Usar o modelo do Deca'}
            </Botao>
          </div>
          {!sujo && email.problemas.length > 0 && (
            <Aviso tom="atencao" titulo="Corrija antes de ligar" className="mt-3">
              <ul className="list-disc pl-5">
                {email.problemas.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </Aviso>
          )}
        </Cartao>
      )}

      {erro !== null && <ErroDaApi titulo="Não foi possível" erro={erro} />}
      {testar.data !== undefined && (
        <Aviso tom={testar.data.ensaio ? 'atencao' : 'positivo'} aoFechar={() => testar.reset()}>
          {testar.data.ensaio
            ? 'O teste foi montado, mas não saiu: neste ambiente o envio de e-mails está em modo de ensaio.'
            : `Teste enviado para ${testar.data.destinatario}, com valores de exemplo.`}
        </Aviso>
      )}
      {restaurada && (
        <Aviso tom="informacao">
          Versão antiga carregada no editor. Ela só vale depois de salvar.
        </Aviso>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="flex flex-col gap-4">
          <Campo
            ref={assuntoRef}
            rotulo="Assunto"
            value={assunto}
            onFocus={() => setAlvo('assunto')}
            onChange={(e) => setAssunto(e.target.value)}
            placeholder="Ex.: Pagamento do crédito de ICMS {{mes}} — {{cliente_nome}}"
          />
          {desconhecidas.length > 0 && (
            <Aviso tom="atencao">
              {desconhecidas.map((v) => `{{${v}}}`).join(', ')}{' '}
              {desconhecidas.length > 1 ? 'não existem' : 'não existe'} neste e-mail e{' '}
              {desconhecidas.length > 1 ? 'sairiam' : 'sairia'} em branco. Troque por uma variável
              da lista ao lado, apague, ou{' '}
              <button
                type="button"
                onClick={() => setConfirmando('so-criar')}
                className="font-medium underline underline-offset-2"
              >
                {desconhecidas.length > 1 ? 'crie estas variáveis' : 'crie esta variável'}
              </button>
              .
            </Aviso>
          )}
          {confirmando !== null && (
            <ConfirmarVariaveis
              tokens={desconhecidas}
              onde={{
                email: email.id,
                reconhecer: (tokens) => reconhecerVariaveisDoEmail(email.id, tokens),
                criar: (lista) => criarVariaveisDoEmail(email.id, lista),
              }}
              rotuloConfirmar={confirmando === 'salvar' ? 'Confirmar e salvar' : 'Criar variáveis'}
              aoCancelar={() => setConfirmando(null)}
              aoConcluir={() => {
                void recarregar();
                if (confirmando === 'salvar') salvar.mutate();
                setConfirmando(null);
              }}
            />
          )}
          <div onFocusCapture={() => setAlvo('corpo')}>
            <EditorDeContrato
              ref={editorRef}
              modo="email"
              value={corpo}
              onChange={setCorpo}
              variaveis={email.variaveis.map((v) => ({ texto: comoInserir(v), rotulo: v.rotulo }))}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Botao
              disabled={!sujo}
              carregando={salvar.isPending}
              onClick={() =>
                desconhecidas.some((v) => /^[a-z][a-z0-9_]*$/.test(v))
                  ? setConfirmando('salvar')
                  : salvar.mutate()
              }
            >
              Salvar
            </Botao>
            <Botao
              variante="secundario"
              carregando={verPrevia.isPending}
              onClick={() => verPrevia.mutate()}
            >
              Pré-visualizar
            </Botao>
            <Botao
              variante="secundario"
              carregando={testar.isPending}
              onClick={() => testar.mutate()}
            >
              Enviar teste para mim
            </Botao>
            {email.modelo !== null && (
              <Botao variante="sutil" onClick={() => setHistorico(true)}>
                Histórico
              </Botao>
            )}
            {sujo && (
              <Botao
                variante="sutil"
                onClick={() => {
                  setAssunto(salvo.assunto);
                  setCorpo(salvo.corpo);
                  setRestaurada(false);
                }}
              >
                Descartar alterações
              </Botao>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-2 lg:sticky lg:top-4 lg:self-start">
          <h3 className="text-sm font-semibold text-conteudo-primario">Variáveis deste e-mail</h3>
          <p className="text-xs text-conteudo-secundario">
            Clique para inserir onde está o cursor — no assunto ou no texto. No envio, cada uma vira
            o dado daquele cliente.
          </p>
          <ul className="flex max-h-[60vh] flex-col gap-1 overflow-y-auto pr-1">
            {email.variaveis.map((v) => (
              <li key={v.nome}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => inserir(comoInserir(v))}
                  className="flex w-full flex-col rounded-card border border-borda px-2.5 py-1.5 text-left transition-colors hover:border-acento-ciano/60 hover:bg-superficie-vidro"
                >
                  <span className="text-sm text-conteudo-primario">{v.rotulo}</span>
                  <span className="font-mono text-xs text-acento-ciano">{comoInserir(v)}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {email.personalizado ? (
        <>
          <QuandoOEmailSai email={email} />
          <UltimosEnvios email={email} />
        </>
      ) : email.automacao !== null ? (
        <AutomacaoDoSistema key={email.id} email={email} automacao={email.automacao} />
      ) : (
        <Cartao
          titulo="Quando este e-mail sai"
          subtitulo="Este e-mail não tem automação para configurar."
          densidade="compacta"
        >
          <p className="text-sm text-conteudo-primario">{email.quando}</p>
        </Cartao>
      )}

      <Modal
        aberto={previa !== null}
        aoFechar={() => setPrevia(null)}
        titulo="Pré-visualização"
        descricao="Com valores de exemplo. No envio, entram os dados de cada cliente."
        largura="ampla"
      >
        {previa !== null && (
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              <span className="text-conteudo-secundario">Assunto:</span>{' '}
              <strong>{previa.assunto}</strong>
            </p>
            <PreviaEmIframe html={previa.html} />
          </div>
        )}
      </Modal>

      {historico && (
        <HistoricoDoModelo
          email={email}
          aoFechar={() => setHistorico(false)}
          aoRestaurar={(v) => {
            setAssunto(v.assunto);
            setCorpo(v.corpo);
            setRestaurada(true);
            setHistorico(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * "Quando este e-mail sai": as automações que mandam este e-mail — as mesmas
 * da aba Automações — e o campo para criar mais uma, descrevendo.
 */
function QuandoOEmailSai(props: { readonly email: EmailDoDeca }): JSX.Element {
  const { email } = props;
  const [rascunho, setRascunho] = useState<RascunhoDeAutomacao | null>(null);
  const rascunhoDoEmail = (): RascunhoDeAutomacao => ({
    nome: `Mandar "${email.rotulo}"`.slice(0, 120),
    definicao: regraDoEmail(email.id),
    ativa: true,
    pedido: null,
  });

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight">
          Quando este e-mail sai
        </h3>
        <p className="text-sm text-conteudo-secundario">
          As automações que mandam este e-mail. Elas também aparecem em Configurações › Automações ›
          De e-mail.
        </p>
      </div>
      <ListaDeAutomacoes
        filtro={(a) => mandaOEmail(a, email.id)}
        vazio="Nenhuma automação manda este e-mail ainda: descreva abaixo quando ele deve sair."
      />
      <NovaAutomacao
        titulo="Nova automação para este e-mail"
        subtitulo="Diga quando ele sai e para quem. A IA mostra o que entendeu, e só vale depois que você aprovar."
        placeholder="Ex.: quando uma negociação do Pontual for concluída, mandar 2 dias depois para o financeiro do fornecedor, com cópia para mim"
        complemento={`O e-mail a mandar é "${email.rotulo}" [${email.id}].`}
        aoAjustar={setRascunho}
        aoCriarSemIa={() => setRascunho(rascunhoDoEmail())}
      />
      <EditorDeAutomacao rascunho={rascunho} aoFechar={() => setRascunho(null)} />
    </section>
  );
}

/**
 * A automação de um e-mail do sistema (etapa 2): o gatilho é o do processo e
 * não muda; aqui se liga ou desliga e se diz para quem vai.
 */
function AutomacaoDoSistema(props: {
  readonly email: EmailDoDeca;
  readonly automacao: NonNullable<EmailDoDeca['automacao']>;
}): JSX.Element {
  const { email, automacao } = props;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const salva = automacao.regra;
  const quandoSalvo = automacao.data?.quando ?? null;
  const [regra, setRegra] = useState<RegraDoEmail>(salva);
  const [quando, setQuando] = useState<QuandoDoEmail | null>(quandoSalvo);
  const [copias, setCopias] = useState(salva.copias.join(', '));
  /** Com agendamentos por sair, a pergunta "reagendar?" — com quantos são. */
  const [pergunta, setPergunta] = useState<number | null>(null);
  const mudouAData = JSON.stringify(quando) !== JSON.stringify(quandoSalvo);
  const sujo = JSON.stringify(regra) !== JSON.stringify(salva) || mudouAData;

  const salvar = useMutation({
    mutationFn: (v: { regra: RegraDoEmail; reagendar?: boolean }) =>
      salvarRegra(email.id, {
        ...v.regra,
        ...(quando === null ? {} : { quando }),
        ...(v.reagendar === undefined ? {} : { reagendar: v.reagendar }),
      }),
    onSuccess: () => {
      setPergunta(null);
      void queryClient.invalidateQueries({ queryKey: CHAVE_DOS_MODELOS });
    },
  });
  const contarAgendados = useMutation({ mutationFn: () => carregarAgendados(email.id) });

  /** Mudou a data e há agendamentos por sair: pergunta antes. Senão, salva. */
  const salvarComPergunta = async (): Promise<void> => {
    if (!mudouAData) {
      salvar.mutate({ regra });
      return;
    }
    const { futuros } = await contarAgendados.mutateAsync();
    if (futuros > 0) setPergunta(futuros);
    else salvar.mutate({ regra, reagendar: false });
  };

  const alternar = async (): Promise<void> => {
    if (salva.ativa) {
      const ok = await dialogo.confirmar({
        titulo: `Desligar "${email.rotulo}"?`,
        descricao: `${automacao.aoDesligar}\n\nO e-mail deixa de sair até alguém ligar de novo aqui.`,
        rotuloConfirmar: 'Desligar',
        tom: 'perigo',
      });
      if (!ok) return;
    }
    salvar.mutate({ regra: { ...salva, ativa: !salva.ativa } });
    setRegra({ ...regra, ativa: !salva.ativa });
  };

  const opcao = (
    valor: RegraDoEmail['para'],
    rotulo: string,
    detalhe?: React.ReactNode,
  ): JSX.Element => (
    <label className="flex cursor-pointer items-start gap-2 text-sm text-conteudo-primario">
      <input
        type="radio"
        name={`para-${email.id}`}
        className="mt-1 accent-acento-indigo"
        checked={regra.para === valor}
        onChange={() =>
          setRegra({
            ...regra,
            para: valor,
            funcao: valor === 'FUNCAO' ? (regra.funcao ?? '') : null,
          })
        }
      />
      <span className="flex flex-col gap-1.5">
        {rotulo}
        {regra.para === valor && detalhe}
      </span>
    </label>
  );

  return (
    <Cartao
      titulo="Quando este e-mail sai"
      subtitulo="A automação deste e-mail."
      acao={
        <div className="flex items-center gap-2">
          <Selo tom={salva.ativa ? 'positivo' : 'atencao'} tamanho="pequeno">
            {salva.ativa ? 'Ligada' : 'Desligada'}
          </Selo>
          <Botao
            variante="secundario"
            tamanho="pequeno"
            carregando={salvar.isPending && !sujo}
            disabled={sujo}
            title={sujo ? 'Salve ou descarte as mudanças antes.' : undefined}
            onClick={() => void alternar()}
          >
            {salva.ativa ? 'Desligar' : 'Ligar'}
          </Botao>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {!salva.ativa && (
          <Aviso tom="atencao" titulo="Desligado: este e-mail não está saindo">
            {automacao.aoDesligar}
          </Aviso>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
            Quando
          </span>
          <p className="text-sm text-conteudo-primario">{automacao.gatilho}</p>
          {automacao.data === null && (
            <p className="text-xs text-conteudo-sutil">
              O momento é o do próprio processo — por isso não muda aqui.
            </p>
          )}
        </div>

        {automacao.data !== null && quando !== null && (
          <EditorDaData
            quando={quando}
            mesDeReferencia={automacao.data.mesDeReferencia}
            aoMudar={setQuando}
          />
        )}

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
            Para quem
          </legend>
          {opcao('SISTEMA', `Quem o sistema manda: ${automacao.paraQuem}`)}
          {automacao.paraEmpresa &&
            opcao(
              'FUNCAO',
              'Só os contatos da empresa com uma função',
              <Campo
                rotulo="Função"
                rotuloOculto
                list="funcoes-do-email"
                maxLength={60}
                placeholder="Ex.: Financeiro"
                ajuda="Parte do cargo cadastrado no contato. Sem ninguém com ela, vai a todos os contatos."
                value={regra.funcao ?? ''}
                onChange={(e) => setRegra({ ...regra, funcao: e.target.value })}
                className="max-w-xs"
              />,
            )}
          {opcao('SO_COPIAS', 'Ninguém além das cópias abaixo')}
          <datalist id="funcoes-do-email">
            {['Financeiro', 'Decisor', 'Contábil', 'Fiscal', 'Diretor'].map((f) => (
              <option key={f} value={f} />
            ))}
          </datalist>
          <Campo
            rotulo="Cópia para"
            placeholder="Ex.: gabriel@ms3capital.com"
            ajuda="Opcional. Endereços que recebem sempre, além de quem está acima. Separe com vírgula."
            value={copias}
            onChange={(e) => {
              setCopias(e.target.value);
              setRegra({
                ...regra,
                copias: e.target.value
                  .split(/[,;\s]+/)
                  .map((x) => x.trim())
                  .filter((x) => x !== ''),
              });
            }}
          />
        </fieldset>

        {automacao.diasAntes && (
          <Campo
            rotulo="Quantos dias antes do vencimento"
            type="number"
            min={1}
            max={60}
            className="max-w-xs"
            ajuda="O lembrete sai às 8h desse dia."
            value={String(regra.diasAntes ?? 5)}
            onChange={(e) =>
              setRegra({
                ...regra,
                diasAntes: Math.max(1, Math.min(60, Math.round(Number(e.target.value) || 5))),
              })
            }
          />
        )}

        {(salvar.error ?? contarAgendados.error) !== null && (
          <ErroDaApi
            titulo="Não foi possível salvar"
            erro={salvar.error ?? contarAgendados.error}
          />
        )}
        {salvar.data !== undefined && salvar.data.reagendados + salvar.data.semCalculo > 0 && (
          <Aviso tom="positivo" aoFechar={() => salvar.reset()}>
            {salvar.data.reagendados} agendamento(s) levado(s) para a data nova.
            {salvar.data.semCalculo > 0 &&
              ` ${salvar.data.semCalculo} sem cálculo ligado ficaram como estavam — ajuste no cronograma do cliente, se precisar.`}
          </Aviso>
        )}
        {sujo && (
          <div className="flex flex-wrap gap-2">
            <Botao
              carregando={salvar.isPending || contarAgendados.isPending}
              onClick={() => void salvarComPergunta()}
            >
              Salvar a automação
            </Botao>
            <Botao
              variante="sutil"
              onClick={() => {
                setRegra(salva);
                setQuando(quandoSalvo);
                setCopias(salva.copias.join(', '));
              }}
            >
              Descartar
            </Botao>
          </div>
        )}
      </div>
      {pergunta !== null && (
        <PerguntaDeReagendar
          futuros={pergunta}
          salvando={salvar.isPending}
          aoEscolher={(reagendar) => salvar.mutate({ regra, reagendar })}
          aoVoltar={() => setPergunta(null)}
        />
      )}
    </Cartao>
  );
}

/** Reagendar os que já estão na fila? As três saídas, ditas por extenso. */
function PerguntaDeReagendar(props: {
  readonly futuros: number;
  readonly salvando: boolean;
  readonly aoEscolher: (reagendar: boolean) => void;
  readonly aoVoltar: () => void;
}): JSX.Element {
  return (
    <Modal
      aberto
      aoFechar={props.aoVoltar}
      titulo="E o que já está agendado?"
      descricao={`Este e-mail tem ${props.futuros} agendamento(s) que ainda não saíram, com a data antiga.`}
      rodape={
        <div className="flex flex-wrap justify-end gap-2">
          <Botao variante="sutil" onClick={props.aoVoltar}>
            Voltar
          </Botao>
          <Botao
            variante="secundario"
            carregando={props.salvando}
            onClick={() => props.aoEscolher(false)}
          >
            Só para os próximos
          </Botao>
          <Botao carregando={props.salvando} onClick={() => props.aoEscolher(true)}>
            Reagendar os {props.futuros}
          </Botao>
        </div>
      }
    >
      <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-conteudo-primario">
        <li>
          <strong>Só para os próximos:</strong> a data nova vale para os contratos fechados daqui em
          diante. Os {props.futuros} já agendados saem na data antiga.
        </li>
        <li>
          <strong>Reagendar os {props.futuros}:</strong> eles vão para a data nova. Se para algum
          ela já passou, ele sai no próximo ciclo do cronograma (em até 10 minutos).
        </li>
      </ul>
    </Modal>
  );
}

/**
 * O dia em que o fluxo mensal agenda o e-mail (etapa 3): o tipo de dia e o
 * número; o mês é o do desenho do fluxo e só aparece escrito. Na transferência,
 * também o piso que deixa o mês entrar no fluxo.
 */
function EditorDaData(props: {
  readonly quando: QuandoDoEmail;
  readonly mesDeReferencia: string;
  readonly aoMudar: (quando: QuandoDoEmail) => void;
}): JSX.Element {
  const { quando, aoMudar } = props;
  const numero = (texto: string, min: number, max: number, padrao: number): number =>
    Math.max(min, Math.min(max, Math.round(Number(texto) || padrao)));

  if ('diasAposFechamento' in quando) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
          Em que dia
        </span>
        <Campo
          rotulo="Dias depois do fechamento do contrato do ano"
          type="number"
          min={0}
          max={60}
          className="max-w-xs"
          ajuda="Dias corridos. 0 = no mesmo dia."
          value={String(quando.diasAposFechamento)}
          onChange={(e) => aoMudar({ diasAposFechamento: numero(e.target.value, 0, 60, 5) })}
        />
      </div>
    );
  }

  const { dia } = quando;
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-conteudo-secundario">
        Em que dia
      </span>
      <div className="grid gap-4 sm:grid-cols-[14rem_8rem_1fr] sm:items-end">
        <CampoDeSelecao
          rotulo="Tipo de dia"
          value={dia.tipo}
          onChange={(e) => {
            const tipo = e.target.value as DiaDoMes['tipo'];
            aoMudar({
              ...quando,
              dia:
                tipo === 'DIA_UTIL'
                  ? { tipo, n: dia.tipo === 'DIA_UTIL' ? dia.n : 1 }
                  : tipo === 'DIA_DO_MES'
                    ? { tipo, dia: dia.tipo === 'DIA_DO_MES' ? dia.dia : 10 }
                    : { tipo },
            });
          }}
          opcoes={[
            { valor: 'DIA_UTIL', rotulo: 'N-ésimo dia útil' },
            { valor: 'DIA_DO_MES', rotulo: 'Dia do calendário' },
            { valor: 'ULTIMO_DIA_UTIL', rotulo: 'Último dia útil' },
          ]}
        />
        {dia.tipo === 'DIA_UTIL' && (
          <Campo
            rotulo="Qual dia útil"
            type="number"
            min={1}
            max={22}
            value={String(dia.n)}
            onChange={(e) =>
              aoMudar({ ...quando, dia: { tipo: 'DIA_UTIL', n: numero(e.target.value, 1, 22, 1) } })
            }
          />
        )}
        {dia.tipo === 'DIA_DO_MES' && (
          <Campo
            rotulo="Dia"
            type="number"
            min={1}
            max={31}
            ajuda="31 em mês de 30 cai no dia 30."
            value={String(dia.dia)}
            onChange={(e) =>
              aoMudar({
                ...quando,
                dia: { tipo: 'DIA_DO_MES', dia: numero(e.target.value, 1, 31, 10) },
              })
            }
          />
        )}
        <p className="pb-2.5 text-sm text-conteudo-secundario sm:col-start-3">
          {props.mesDeReferencia}
        </p>
      </div>
      <p className="text-sm text-conteudo-primario">
        Fica: <strong>{`no ${descreverDia(dia)} ${props.mesDeReferencia}`}</strong>.
      </p>
      {quando.pisoPercentual !== undefined && (
        <Campo
          rotulo="Só meses com limite de utilização acima de (%)"
          type="number"
          min={0}
          max={100}
          className="max-w-xs"
          ajuda="O mês com limite igual ou abaixo não entra no fluxo. Vale para os próximos contratos fechados."
          value={String(quando.pisoPercentual)}
          onChange={(e) =>
            aoMudar({ ...quando, pisoPercentual: numero(e.target.value, 0, 100, 10) })
          }
        />
      )}
    </div>
  );
}

const SITUACAO_DO_ENVIO: Readonly<Record<SituacaoDoEnvio, { tom: TomSemantico; texto: string }>> = {
  AGENDADO: { tom: 'informativo', texto: 'Agendado' },
  ENVIANDO: { tom: 'progresso', texto: 'Saindo' },
  ENVIADO: { tom: 'positivo', texto: 'Enviado' },
  ENSAIO: { tom: 'neutro', texto: 'Ensaio (não saiu)' },
  FALHOU: { tom: 'critico', texto: 'Falhou' },
  CANCELADO: { tom: 'neutro', texto: 'Cancelado' },
};

/** Os últimos envios deste e-mail pelas automações — com o que está agendado, para cancelar. */
function UltimosEnvios(props: { readonly email: EmailDoDeca }): JSX.Element {
  const chave = [...CHAVE_DOS_MODELOS, props.email.id, 'envios'];
  const queryClient = useQueryClient();
  const envios = useQuery({
    queryKey: chave,
    queryFn: () => carregarEnvios(props.email.id),
    refetchInterval: 30_000,
  });
  const cancelar = useMutation({
    mutationFn: cancelarEnvio,
    onSettled: () => void queryClient.invalidateQueries({ queryKey: chave }),
  });

  return (
    <Cartao
      titulo="Últimos envios"
      subtitulo="O que as automações mandaram (ou vão mandar) deste e-mail."
      densidade="compacta"
    >
      {cancelar.error !== null && (
        <ErroDaApi titulo="Não foi possível cancelar" erro={cancelar.error} />
      )}
      {envios.isError ? (
        <ErroDaApi titulo="Não foi possível carregar os envios" erro={envios.error} />
      ) : envios.data === undefined ? (
        <EstadoCarregando contexto="os envios" />
      ) : envios.data.length === 0 ? (
        <p className="text-sm text-conteudo-sutil">Nenhum envio ainda.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-borda">
          {envios.data.map((e: EnvioDoEmail) => (
            <li
              key={e.id}
              className="flex flex-col gap-1 py-2.5 sm:flex-row sm:items-center sm:gap-3"
            >
              <Selo tom={SITUACAO_DO_ENVIO[e.situacao].tom} tamanho="pequeno">
                {SITUACAO_DO_ENVIO[e.situacao].texto}
              </Selo>
              <div className="min-w-0 flex-1 text-sm">
                <p className="text-conteudo-primario">
                  {e.empresa ?? '—'}{' '}
                  <span className="text-conteudo-sutil">
                    · {new Date(e.enviarEm).toLocaleString('pt-BR')} · {e.automacao}
                  </span>
                </p>
                {e.destinatarios.length > 0 && (
                  <p className="truncate text-xs text-conteudo-secundario">
                    Para: {e.destinatarios.join(', ')}
                  </p>
                )}
                {e.erro !== null && <p className="text-xs text-atencao">{e.erro}</p>}
              </div>
              {e.situacao === 'AGENDADO' && (
                <Botao
                  variante="sutil"
                  tamanho="pequeno"
                  carregando={cancelar.isPending && cancelar.variables === e.id}
                  onClick={() => cancelar.mutate(e.id)}
                >
                  Cancelar
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}
    </Cartao>
  );
}

function HistoricoDoModelo(props: {
  readonly email: EmailDoDeca;
  readonly aoFechar: () => void;
  readonly aoRestaurar: (v: { assunto: string; corpo: string }) => void;
}): JSX.Element {
  const versoes = useQuery({
    queryKey: [...CHAVE_DOS_MODELOS, props.email.id, 'versoes'],
    queryFn: () => carregarVersoes(props.email.id),
  });

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={`Histórico — ${props.email.rotulo}`}
      descricao="As últimas 30 versões salvas. Restaurar carrega a versão no editor; ela só vale depois de salvar."
      largura="larga"
    >
      {versoes.isError ? (
        <ErroDaApi titulo="Não foi possível carregar o histórico" erro={versoes.error} />
      ) : versoes.data === undefined ? (
        <EstadoCarregando contexto="o histórico" />
      ) : (
        <ul className="flex flex-col divide-y divide-borda">
          {versoes.data.map((v, i) => (
            <li key={v.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1 text-sm">
                <p className="font-medium text-conteudo-primario">
                  {new Date(v.criadaEm).toLocaleString('pt-BR')} — {v.motivo}
                  {i === 0 && ' (atual)'}
                </p>
                <p className="truncate text-conteudo-secundario">
                  {v.autor ?? 'Sem autor'} · {v.assunto}
                </p>
              </div>
              {i > 0 && (
                <Botao variante="secundario" tamanho="pequeno" onClick={() => props.aoRestaurar(v)}>
                  Restaurar
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/** O e-mail num iframe isolado: o HTML dele não herda nem vaza estilo da tela. */
function PreviaEmIframe(props: { readonly html: string }): JSX.Element {
  return (
    <iframe
      title="Pré-visualização do e-mail"
      srcDoc={props.html}
      sandbox=""
      className="h-[70vh] w-full rounded-card border border-borda bg-white"
    />
  );
}
